import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SaviBusinessJsonLd from "../../components/SaviBusinessJsonLd";
import SaviQuiz from "../../components/SaviQuiz";
import { ArrowRight, Target, Users, Zap, TrendingUp, Clock, Shield } from "lucide-react";
import { pic } from "../../lib/pic";

const ColivingFoundersPage = () => {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Coliving for Founders in Valencia | Savi — Build, Ship, Focus</title>
        <meta name="description" content="A coliving in Valencia built for founders and entrepreneurs. Sober, focused, no distractions. Ship your product surrounded by people doing the same. From €1,200/month." />
        <link rel="canonical" href="https://www.savicoliving.com/coliving-valencia-for-founders" />
        <meta property="og:title" content="Coliving Valencia for Founders | Savi Coliving" />
        <meta property="og:description" content="Stop talking about building. Actually build. A focused coliving for founders in the mountains near Valencia." />
      </Helmet>
      <SaviBusinessJsonLd id="coliving-founders" />

      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FBF7F1]/95 backdrop-blur-lg border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" alt="Savi" className="h-7 w-auto" />
            <span className="text-base tracking-wider text-[#C89B7B]"><span style={{fontFamily: 'Cinzel, serif'}}>SAVI</span> <span style={{fontFamily: "'Cormorant Garamond', serif"}}>Coliving</span></span>
          </Link>
          <Link to="/" className="text-sm text-[#C85A38] font-medium hover:underline">View the house →</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-14">
        <div className="relative h-[70vh] overflow-hidden">
          <img {...pic("room-suite")} fetchpriority="high" decoding="async" alt="Coliving for founders near Valencia" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2E2A26] via-[#2E2A26]/50 to-[#2E2A26]/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4 max-w-4xl leading-tight" style={{textShadow: '0 2px 12px rgba(0,0,0,0.5)'}}>
              Stop talking about building.<br className="hidden sm:block" /> Actually build.
            </h1>
            <p className="text-white/60 text-base sm:text-lg max-w-xl mb-8" style={{textShadow: '0 2px 8px rgba(0,0,0,0.4)'}}>
              A coliving in Valencia for founders who need to ship. No distractions. No alcohol. Just a focused house full of people doing the same thing.
            </p>
            <Link to="/">
              <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-4 text-sm font-medium tracking-wide transition-all">
                View the house <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* THE FOUNDER PROBLEM */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-5">The founder isolation problem</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Building something is lonely. You're making decisions alone, working alone, and the people around you — partners, family, friends — often don't understand why you'd leave a good job to grind on something uncertain.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Most <strong className="text-[#2E2A26]/80">coliving for founders</strong> programs try to fix this with hackathons and pitch nights. That's not what you need. You need a house full of people who understand the weight of what you're carrying — without having to explain it.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            That's Savi. Not an accelerator. Not a networking event. Just a house in the mountains near Valencia where founders, freelancers, and builders live and work together for 1-3 months.
          </p>
        </div>
      </section>

      {/* WHAT FOUNDERS GET */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">What founders actually need</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Target, title: "Focus time", text: "A proper coworking space with dual screens, standing desks, and 500Mbps Starlink. No coffee-shop WiFi lottery." },
              { icon: Users, title: "Peer accountability", text: "Weekly accountability sessions. Not forced. Just people naturally pushing each other because they're in the same fight." },
              { icon: Zap, title: "Skill shares", text: "Someone knows design, someone knows fundraising, someone knows ops. Organic knowledge sharing happens when people live together." },
              { icon: Shield, title: "Zero distractions", text: "No alcohol, no party culture, no drama. You came here to work. The environment protects that." },
              { icon: TrendingUp, title: "Momentum", text: "Hard to procrastinate when the person across the table just shipped a feature. The energy is contagious." },
              { icon: Clock, title: "Routine", text: "Mountain mornings, deep work blocks, gym, group dinners. A rhythm that actually serves building — not destroying." }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#C85A38]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <p className="text-[#2E2A26] text-sm font-medium mb-1">{item.title}</p>
                  <p className="text-[#2E2A26]/60 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">From people who've been here</h2>
          <div className="space-y-4">
            {[
              { quote: "A rare environment where you can slow down, connect, and leave clearer, more focused, and recharged than when you arrived. When I'm back in Spain, this is where I'll come home to.", name: "Travis Weeks", detail: "Resident, 90-day stay", img: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/tc416uth_travis.jpg" },
              { quote: "I genuinely couldn't have found a better co-living spot in Valencia. The location is incredible. The community is warm. The standard of hosting is on another level.", name: "Devinn", detail: "Resident, Google Local Guide", img: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/ovbdty6h_devvo.jpg" }
            ].map((t, i) => (
              <blockquote key={i} className="bg-[#F4ECE2] rounded-xl p-5">
                <p className="text-[#2E2A26]/70 text-sm leading-relaxed mb-3">"{t.quote}"</p>
                <div className="flex items-center gap-2.5">
                  <img src={t.img} alt={t.name} className="w-8 h-8 rounded-full object-cover" loading="lazy" />
                  <p className="text-[#2E2A26]/90 text-xs font-medium">{t.name} <span className="text-[#2E2A26]/40">— {t.detail}</span></p>
                </div>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* NOT AN ACCELERATOR */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-5">Not an accelerator. Not a retreat.</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            There's no pitch competition. No mentors assigned to you. No one's taking equity. Savi is a place to live and work, not a program.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            The value is the environment: a sober, focused, distraction-free house with people who are building real things. The community forms naturally because everyone's aligned. No one needs to be "activated."
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Rooms from €1,200/month. Stay 1-3 months. Everything included — coworking, WiFi, gym, pool, all utilities. You just show up and work.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            Further along, with a business that works and a founder who doesn't? Savi also runs <Link to="/sojourns/burnout-retreat-for-business-owners" className="text-[#C85A38] hover:underline">a month-long mentored retreat for business owners</Link>, spring 2027, by application.
          </p>
        </div>
      </section>

      {/* QUIZ */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-xl mx-auto text-center mb-8">
          <p className="text-[#C85A38] text-xs font-medium tracking-[0.2em] uppercase mb-2">30 seconds</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-2">Is Savi right for you?</h2>
          <p className="text-[#2E2A26]/50 text-sm">Four quick questions. No sign-up required.</p>
        </div>
        <SaviQuiz />
      </section>

      {/* CTA */}
      <section className="py-14 px-4 bg-[#2E2A26]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">Ready to ship?</h2>
          <p className="text-white/50 text-sm mb-8 max-w-md mx-auto">
            We review every application. If you're a founder working on something real and you want an environment that actually supports that — take a look.
          </p>
          <Link to="/">
            <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-12 py-5 text-sm font-medium transition-all">
              View Savi Coliving <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-8 px-4 bg-[#FBF7F1]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#2E2A26]/30 text-xs uppercase tracking-wider mb-3">Related</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/quiet-coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Quiet Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/luxury-coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Luxury Coliving Valencia</Link>
          </div>
        </div>
      </section>

      <footer className="bg-white py-6 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#2E2A26]/40 text-xs">&copy; 2026 SAVI Coliving</p>
          <div className="flex gap-4 text-xs text-[#2E2A26]/40">
            <Link to="/privacy" className="hover:text-[#2E2A26]">Privacy</Link>
            <Link to="/cookies" className="hover:text-[#2E2A26]">Cookies</Link>
            <Link to="/" className="hover:text-[#2E2A26]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ColivingFoundersPage;
